// src/PatientPortalHome/PatientPortalHome.jsx
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth, db } from "../firebase";
import { signOut } from "firebase/auth";
import "./PatientPortalHome.css";

import {
  Stethoscope,
  Calendar,
  FileText,
  HeartPulse,
  Pill,
  MessageCircle,
  ChevronsUpDown,
  MapPin,
  Menu,
  Search,
  LogOut,
  User,
  X,
} from "lucide-react";

import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  limit,
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  addDoc,
} from "firebase/firestore";

import LogoMikDok from "../components/common/LogoMikDok";
import PromoSpot from "../components/common/PromoSpot";

/* -------------------- SMALL PRESENTATIONAL COMPONENTS -------------------- */

const Tile = ({ icon: Icon, label, badge, onClick }) => (
  <button className="tile" onClick={onClick}>
    <div className="tile-icon">
      <Icon className="tile-icon-svg" />
      {badge && <span className="tile-badge">{badge}</span>}
    </div>
    <span className="tile-label">{label}</span>
  </button>
);

const QuickCard = ({ icon: Icon, title, subtitle, onClick, badge }) => (
  <button className="quick-card" onClick={onClick}>
    <div className="quick-card-icon">
      <Icon className="quick-card-icon-svg" />
      {badge && <span className="quick-card-badge">{badge}</span>}
    </div>
    <div className="quick-card-text">
      <p className="quick-card-title">{title}</p>
      <p className="quick-card-subtitle">{subtitle}</p>
    </div>
  </button>
);

/* -------------------- HELPERS -------------------- */

// Convert Firestore Timestamp / string / Date → JS Date or null
const toJsDate = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value.toDate === "function") {
    // Firestore Timestamp
    return value.toDate();
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const isPromoActive = (promo) => {
  if (!promo) return false;
  if (promo.active === false) return false;

  const now = new Date();

  if (promo.validFrom) {
    const from = new Date(promo.validFrom);
    from.setHours(0, 0, 0, 0);
    if (now < from) return false;
  }

  if (promo.validTo) {
    const to = new Date(promo.validTo);
    to.setHours(23, 59, 59, 999);
    if (now > to) return false;
  }

  return true;
};

// shuffle helper so promos appear in random order on each load
const shuffle = (arr = []) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/* -------------------- MAIN COMPONENT -------------------- */

export default function PatientPortalHome() {
  const [user] = useAuthState(auth);
  const navigate = useNavigate();

  // promos
  const [mainPromos, setMainPromos] = useState([]);
  const [secondaryPromos, setSecondaryPromos] = useState([]);
  const [chosenMainPromo, setChosenMainPromo] = useState(null);
  const [chosenSecondaryPromo, setChosenSecondaryPromo] = useState(null);

  // user info
  const [displayName, setDisplayName] = useState("Përdorues");
  const [lastSeenHomeAt, setLastSeenHomeAt] = useState(null);

  // location
  const [city, setCity] = useState("Tirana");
  const [locLoading, setLocLoading] = useState(false);

  // UI
  const [showMenu, setShowMenu] = useState(false);

  // appointments strip
  const [nextAppointment, setNextAppointment] = useState(null);
  const [lastAppointment, setLastAppointment] = useState(null);

  // "new" indicators
  const [hasNewRecords, setHasNewRecords] = useState(false);
  const [hasNewMessages, setHasNewMessages] = useState(false);

  // tiny toast notification + guard for not spamming alerts
  const [notification, setNotification] = useState(null);
  const notifiedRef = useRef({
    messages: false,
    records: false,
  });

  /* -------------------- HELPERS INSIDE COMPONENT -------------------- */

  const open = (path) => {
    if (!user) return navigate("/login");
    navigate(path);
  };

  const markHomeSeen = async () => {
    if (!user) return;
    try {
      const profileRef = doc(db, "users", user.uid);
      await updateDoc(profileRef, {
        lastSeenHomeAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("Failed to update lastSeenHomeAt:", err);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error logging out:", error);
    }
  };

  // Record promo metrics for patients
  const trackPatientPromoEvent = useCallback(
    async (promo, type) => {
      try {
        if (!promo?.id || !user?.uid) return;

        const eventsRef = collection(db, "promoEvents");
        const payload = {
          promoId: promo.id,
          slot: promo.slot || "patient-home-main",
          audience: "patient",
          patientId: user.uid,
          type, // "impression" | "view" | "click"
          createdAt: serverTimestamp(),
        };

        await addDoc(eventsRef, payload);
      } catch (err) {
        console.warn("[promo metrics] Failed to track patient promo event", type, err);
      }
    },
    [user]
  );

  // Navigation for promos – tracking for secondary & generic usage
  const handlePromoClick = (promo, { track = true } = {}) => {
    if (!promo) return;

    if (track) {
      trackPatientPromoEvent(promo, "click");
    }

    const targetType =
      promo.targetType || (promo.externalUrl ? "external" : "internal");

    if (targetType === "external" && promo.externalUrl) {
      const newTab = promo.openInNewTab ?? true;
      window.open(promo.externalUrl, newTab ? "_blank" : "_self");
      return;
    }

    if (promo.ctaPath) {
      open(promo.ctaPath);
    }
  };

  const toApptDate = (appt) => {
    if (!appt?.scheduledDate) return null;
    const dateStr = appt.scheduledDate;
    const timeStr = appt.scheduledTime || "00:00";
    const iso = `${dateStr}T${timeStr}`;
    const d = new Date(iso);
    return isNaN(d.getTime()) ? null : d;
  };

  const formatApptDateLabel = (appt) => {
    const d = toApptDate(appt);
    if (!d) return "Pa datë";

    const now = new Date();
    const isToday =
      d.getFullYear() === now.getFullYear() &&
      d.getMonth() === now.getMonth() &&
      d.getDate() === now.getDate();

    const time = d.toLocaleTimeString("sq-AL", {
      hour: "2-digit",
      minute: "2-digit",
    });

    const dayLabel = isToday
      ? "Sot"
      : d.toLocaleDateString("sq-AL", {
          day: "2-digit",
          month: "2-digit",
        });

    return `${dayLabel} • ${time}`;
  };

  const getStatusChip = (statusRaw) => {
    const status = (statusRaw || "").toLowerCase();
    if (status === "confirmed")
      return {
        label: "Konfirmuar",
        className: "appt-status appt-status-confirmed",
      };
    if (status === "completed")
      return {
        label: "Përfunduar",
        className: "appt-status appt-status-completed",
      };
    if (status === "cancelled")
      return {
        label: "Anuluar",
        className: "appt-status appt-status-cancelled",
      };
    return { label: "Në pritje", className: "appt-status appt-status-pending" };
  };

  /* -------------------- EFFECTS -------------------- */

  // Geolocation for city
  useEffect(() => {
    if (!("geolocation" in navigator)) {
      return;
    }

    setLocLoading(true);

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;

          const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}`;

          const res = await fetch(url, {
            headers: {
              "Accept-Language": "sq,en;q=0.8",
            },
          });

          if (!res.ok) {
            setLocLoading(false);
            return;
          }

          const data = await res.json();
          const addr = data.address || {};

          const cityName =
            addr.city ||
            addr.town ||
            addr.village ||
            addr.municipality ||
            addr.state;

          if (cityName) {
            setCity(cityName);
          }

          setLocLoading(false);
        } catch (err) {
          console.error("Error resolving city:", err);
          setLocLoading(false);
        }
      },
      (err) => {
        console.warn("Geolocation error:", err);
        setLocLoading(false);
      },
      {
        enableHighAccuracy: false,
        timeout: 8000,
        maximumAge: 60_000,
      }
    );
  }, []);

  // Load user profile + lastSeenHomeAt
  useEffect(() => {
    if (!user) {
      setDisplayName("Përdorues");
      setLastSeenHomeAt(null);
      return;
    }

    const authName = (user.fullName || "").trim();
    const emailLocal = (user.email || "").split("@")[0];
    const prettyEmail =
      emailLocal.length > 0
        ? emailLocal.charAt(0).toUpperCase() + emailLocal.slice(1)
        : "";

    setDisplayName(authName || prettyEmail || "Përdorues");

    const fetchProfile = async () => {
      try {
        const profileRef = doc(db, "users", user.uid);
        const snap = await getDoc(profileRef);

        if (snap.exists()) {
          const data = snap.data();
          const full = (data.fullName || data.name || "").toString().trim();
          if (full) setDisplayName(full);

          if (
            data.lastSeenHomeAt &&
            typeof data.lastSeenHomeAt.toDate === "function"
          ) {
            setLastSeenHomeAt(data.lastSeenHomeAt.toDate());
          }
        }
      } catch (err) {
        console.error("Error loading profile name from users:", err);
      }
    };

    fetchProfile();
  }, [user]);

  // Watch medicalRecords to detect "new" results
  useEffect(() => {
    if (!user) return;

    const qRec = query(
      collection(db, "medicalRecords"),
      where("patientId", "==", user.uid),
      orderBy("updatedAt", "desc"),
      limit(20)
    );

    const unsub = onSnapshot(
      qRec,
      (snap) => {
        if (snap.empty) {
          console.log("[medicalRecords] no docs");
          setHasNewRecords(false);
          return;
        }

        const latest = snap.docs[0].data();
        const ts = latest.updatedAt || latest.createdAt;

        if (!ts || typeof ts.toDate !== "function") {
          console.log("[medicalRecords] latest has no timestamp");
          setHasNewRecords(false);
          return;
        }

        const latestDt = ts.toDate();

        console.log(
          "[medicalRecords] latestDt:",
          latestDt,
          "lastSeenHomeAt:",
          lastSeenHomeAt
        );

        if (!lastSeenHomeAt) {
          setHasNewRecords(true);
          return;
        }

        setHasNewRecords(latestDt > lastSeenHomeAt);
      },
      (err) => {
        console.error("Error watching medicalRecords:", err);
        setHasNewRecords(false);
      }
    );

    return () => unsub();
  }, [user, lastSeenHomeAt]);

  // Watch conversations for new messages
  useEffect(() => {
    if (!user) return;

    const qConv = query(
      collection(db, "conversations"),
      where("patientId", "==", user.uid),
      orderBy("lastMessageAt", "desc"),
      limit(20)
    );

    const unsub = onSnapshot(
      qConv,
      (snap) => {
        if (snap.empty) {
          console.log("[conversations] no docs");
          setHasNewMessages(false);
          return;
        }

        const latest = snap.docs[0].data();
        const ts = latest.lastMessageAt;

        if (!ts || typeof ts.toDate !== "function") {
          console.log("[conversations] latest has no lastMessageAt");
          setHasNewMessages(false);
          return;
        }

        const latestDt = ts.toDate();

        console.log(
          "[conversations] latestDt:",
          latestDt,
          "lastSeenHomeAt:",
          lastSeenHomeAt
        );

        if (!lastSeenHomeAt) {
          setHasNewMessages(true);
          return;
        }

        setHasNewMessages(latestDt > lastSeenHomeAt);
      },
      (err) => {
        console.error("Error watching conversations:", err);
        setHasNewMessages(false);
      }
    );

    return () => unsub();
  }, [user, lastSeenHomeAt]);

  // Load patient appointments for strip
  useEffect(() => {
    if (!user) return;

    const qAppts = query(
      collection(db, "appointments"),
      where("patientId", "==", user.uid),
      orderBy("createdAt", "desc"),
      limit(25)
    );

    const unsub = onSnapshot(
      qAppts,
      (snap) => {
        const list = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        if (!list.length) {
          setNextAppointment(null);
          setLastAppointment(null);
          return;
        }

        const last = list[0];
        setLastAppointment(last);

        const now = new Date();

        const upcomingCandidates = list
          .filter((a) => a.status !== "cancelled")
          .map((a) => ({ ...a, _dt: toApptDate(a) }))
          .filter((a) => a._dt && a._dt >= now)
          .sort((a, b) => a._dt - b._dt);

        setNextAppointment(upcomingCandidates[0] || null);
      },
      (err) => {
        console.error("Failed to load appointments for strip:", err);
      }
    );

    return () => unsub();
  }, [user]);

  // listen live to promos (ONLY patient audience)
  useEffect(() => {
    const mainQ = query(
      collection(db, "promoSlots"),
      where("audience", "==", "patient"),
      where("slot", "==", "patient-home-main"),
      orderBy("priority", "asc")
    );
    const secondaryQ = query(
      collection(db, "promoSlots"),
      where("audience", "==", "patient"),
      where("slot", "==", "patient-home-secondary"),
      orderBy("priority", "asc")
    );

    const unsubMain = onSnapshot(
      mainQ,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMainPromos(list);
      },
      (err) => {
        console.error("Error loading main promos:", err);
      }
    );

    const unsubSecondary = onSnapshot(
      secondaryQ,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setSecondaryPromos(list);
      },
      (err) => {
        console.error("Error loading secondary promos:", err);
      }
    );

    return () => {
      unsubMain();
      unsubSecondary();
    };
  }, []);

  const fallbackMain = {
    topLabel: "MIKDOK FOKUS",
    title: "Kontroll kardiologjik me çmim të reduktuar.",
    subtitle: "Rezervo analizat dhe merr përgjigjet direkt në MikDok.",
    ctaLabel: "Rezervo",
    ctaPath: "/patient/records",
  };

  const fallbackSecondary = {
    topLabel: "Ofertë laborator",
    title: "Check-up bazë familjar",
    subtitle: "Rezultatet direkt në app.",
    ctaLabel: "Rezervo tani",
    ctaPath: "/patient/records",
  };

  // choose one promo per slot per reload
  useEffect(() => {
    const active = mainPromos.filter(isPromoActive);
    const list = active.length > 0 ? active : [fallbackMain];

    if (list.length === 0) {
      setChosenMainPromo(null);
      return;
    }

    const selected = shuffle(list)[0];
    setChosenMainPromo(selected);
  }, [mainPromos]);

  useEffect(() => {
    const active = secondaryPromos.filter(isPromoActive);
    const list = active.length > 0 ? active : [fallbackSecondary];

    if (list.length === 0) {
      setChosenSecondaryPromo(null);
      return;
    }

    const selected = shuffle(list)[0];
    setChosenSecondaryPromo(selected);
  }, [secondaryPromos]);

  // Track impressions when a promo is actually selected
  useEffect(() => {
    if (chosenMainPromo && user) {
      trackPatientPromoEvent(chosenMainPromo, "impression");
    }
  }, [chosenMainPromo, user, trackPatientPromoEvent]);

  useEffect(() => {
    if (chosenSecondaryPromo && user) {
      trackPatientPromoEvent(chosenSecondaryPromo, "impression");
    }
  }, [chosenSecondaryPromo, user, trackPatientPromoEvent]);

  /* -------------------- NEW: NOTIFICATION + VIBRATION LOGIC -------------------- */

  // Whenever there are new messages
  useEffect(() => {
    if (hasNewMessages && !notifiedRef.current.messages) {
      notifiedRef.current.messages = true;

      setNotification("Keni mesazhe të reja nga mjeku.");

      if ("vibrate" in navigator) {
        navigator.vibrate([40, 80, 40]); // small pattern
      }
    }

    // reset guard when user has no new messages anymore
    if (!hasNewMessages) {
      notifiedRef.current.messages = false;
    }
  }, [hasNewMessages]);

  // Whenever there are new medical records
  useEffect(() => {
    if (hasNewRecords && !notifiedRef.current.records) {
      notifiedRef.current.records = true;

      setNotification("Janë shtuar rezultate të reja mjekësore.");

      if ("vibrate" in navigator) {
        navigator.vibrate([40, 80, 40]);
      }
    }

    if (!hasNewRecords) {
      notifiedRef.current.records = false;
    }
  }, [hasNewRecords]);

  // auto-hide notification after a few seconds
  useEffect(() => {
    if (!notification) return;
    const id = setTimeout(() => setNotification(null), 5000);
    return () => clearTimeout(id);
  }, [notification]);

  /* -------------------- RENDER -------------------- */

  return (
    <div className="page-wrapper">
      {/* HEADER */}
      <header className="header">
        <div className="location-pill">
          <span className="location-dot" />
          <span className="location-text">
            {locLoading ? "Duke gjetur…" : city}
          </span>
        </div>

        <LogoMikDok className="logo" />

        <button
          onClick={() => setShowMenu(!showMenu)}
          className="menu-btn"
          aria-label="Hape menunë"
        >
          <Menu className="menu-icon" />
        </button>
      </header>

      {/* MENU DROPDOWN */}
      {showMenu && (
        <>
          <div className="menu-overlay" onClick={() => setShowMenu(false)} />
          <div className="menu-dropdown">
            <div className="menu-header">
              <h3 className="menu-title">Menu</h3>
              <button
                className="menu-close"
                onClick={() => setShowMenu(false)}
                aria-label="Mbyll menunë"
              >
                <X size={24} />
              </button>
            </div>

            <div className="menu-user-info">
              <div className="menu-user-avatar">
                <User size={24} />
              </div>
              <div className="menu-user-details">
                <p className="menu-user-name">{displayName}</p>
                <p className="menu-user-email">{user?.email}</p>
              </div>
            </div>

            <div className="menu-divider" />

            <button
              className="menu-item"
              onClick={() => {
                setShowMenu(false);
                navigate("/patient-profile");
              }}
            >
              <User size={20} />
              <span>Profili im</span>
            </button>

            <div className="menu-divider" />

            <button
              className="menu-item menu-item-logout"
              onClick={handleLogout}
            >
              <LogOut size={20} />
              <span>Dil</span>
            </button>
          </div>
        </>
      )}

      {/* GREETING */}
      <section className="greeting">
        <p className="greet-small">
          Mirë se erdhe, <strong>{displayName}</strong>
        </p>
        <h1 className="greet-title">
          Kujdesu për shëndetin tënd <br /> në një vend të vetëm.
        </h1>
      </section>

      {/* SEARCH BAR */}
      <section className="search-section">
        <button
          onClick={() => navigate("/patient/explore")}
          className="search-bar"
        >
          <Search className="search-icon" />
          <span className="search-placeholder">
            Kërko mjek, klinikë ose simptomë…
          </span>
        </button>
      </section>

      {/* APPOINTMENT QUICK CTA STRIP */}
      {(nextAppointment || lastAppointment) && (
        <section className="appointments-strip">
          {nextAppointment && (
            <button
              className="appt-card"
              onClick={() => open("/patient/appointments")}
            >
              <div className="appt-card-header">
                <span className="appt-card-label">Takimi i ardhshëm</span>
                {(() => {
                  const chip = getStatusChip(nextAppointment.status);
                  return <span className={chip.className}>{chip.label}</span>;
                })()}
              </div>
              <div className="appt-card-main">
                <div className="appt-card-title">
                  {nextAppointment.doctorInfo?.name ||
                    nextAppointment.doctorName ||
                    "Mjeku"}
                </div>
                <div className="appt-card-meta">
                  <Calendar size={14} />
                  <span>{formatApptDateLabel(nextAppointment)}</span>
                </div>
              </div>
            </button>
          )}

          {lastAppointment &&
            (!nextAppointment || nextAppointment.id !== lastAppointment.id) && (
              <button
                className="appt-card appt-card-secondary"
                onClick={() => open("/patient/appointments")}
              >
                <div className="appt-card-header">
                  <span className="appt-card-label">Takimi i fundit</span>
                  {(() => {
                    const chip = getStatusChip(lastAppointment.status);
                    return <span className={chip.className}>{chip.label}</span>;
                  })()}
                </div>
                <div className="appt-card-main">
                  <div className="appt-card-title">
                    {lastAppointment.doctorInfo?.name ||
                      lastAppointment.doctorName ||
                      "Mjeku"}
                  </div>
                  <div className="appt-card-meta">
                    <Calendar size={14} />
                    <span>{formatApptDateLabel(lastAppointment)}</span>
                  </div>
                </div>
              </button>
            )}
        </section>
      )}

      {/* PRIMARY CTA */}
      <section className="primary-cta-section">
        <button
          className="primary-cta-card"
          onClick={() => open("/patient/explore")}
        >
          <div className="primary-cta-icon">
            <Calendar className="primary-cta-icon-svg" />
          </div>
          <div className="primary-cta-text">
            <p className="primary-cta-title">Rezervo takimin tënd</p>
            <p className="primary-cta-subtitle">
              Shiko ose shto një takim me mjekun në pak sekonda.
            </p>
          </div>
        </button>
      </section>

      {/* QUICK STATUS STRIP */}
      <section className="quick-strip">
        <QuickCard
          icon={Calendar}
          title="Takimet"
          subtitle="Takimet e rezervuara dhe historiku."
          onClick={() => open("/patient/appointments")}
        />
        <QuickCard
          icon={MessageCircle}
          title="Mesazhet"
          subtitle="Komunikimi me mjekët."
          onClick={() => {
            markHomeSeen(); // this will update lastSeenHomeAt going forward
            open("/patient/messages");
          }}
          badge={hasNewMessages ? "New" : null}
        />

        <QuickCard
          icon={HeartPulse}
          title="Rezultatet"
          subtitle="Rekordet dhe analizat e ruajtura."
          onClick={() => {
            markHomeSeen(); // optional: mark seen when entering records
            open("/patient/records");
          }}
          // empty string still renders a dot thanks to CSS
          badge={hasNewRecords ? "" : null}
        />
      </section>

      {/* MAIN CONTENT SECTIONS */}
      <main className="main-content">
        {/* SHËNDETI IM */}
        <section className="section-block">
          <div className="section-header">
            <h2 className="section-title">Shëndeti im</h2>
            <p className="section-subtitle">
              Gjithçka për shëndetin tënd në një vend.
            </p>
          </div>
          <div className="grid grid-health">
            <Tile
              icon={Calendar}
              label="Takimet"
              onClick={() => open("/patient/appointments")}
            />
            <Tile
              icon={HeartPulse}
              label="Historiku mjekësor"
              onClick={() => open("/patient/records")}
            />
            <Tile
              icon={FileText}
              label="Dokumentet e ngarkuara"
              onClick={() => open("/patient/documents")}
            />
            <Tile
              icon={Pill}
              label="Recetat"
              onClick={() => open("/patient/prescriptions")}
            />
          </div>
        </section>

        {/* ZBULO & KËRKO */}
        <section className="section-block">
          <div className="section-header">
            <h2 className="section-title">Zbulo & kërko</h2>
            <p className="section-subtitle">
              Gjej mjekun e duhur dhe informacion të dobishëm.
            </p>
          </div>
          <div className="grid grid-explore">
            <Tile
              icon={Stethoscope}
              label="Gjej mjek"
              onClick={() => open("/patient/explore")}
            />
            <Tile
              icon={ChevronsUpDown}
              label="Postime"
              onClick={() => open("/patient/posts")}
            />
            <Tile
              icon={MapPin}
              label="Harta"
              onClick={() => open("/patient/map")}
            />
            <Tile
              icon={MessageCircle}
              label="Mesazhe"
              onClick={() => open("/patient/messages")}
            />
          </div>
        </section>
      </main>

      {/* MAIN BANNER via PromoSpot */}
      {chosenMainPromo && (
        <section className="banner-main">
          <div className="banner-main-card">
            <PromoSpot
              promo={chosenMainPromo}
              onCtaClick={(promo) => handlePromoClick(promo, { track: false })}
              onExpand={(promo) => trackPatientPromoEvent(promo, "view")}
              onClickTrack={(promo) => trackPatientPromoEvent(promo, "click")}
            />
          </div>
        </section>
      )}

      {/* SECONDARY BANNER – keep your own layout but now tracked */}
      {chosenSecondaryPromo && (
         <section className="banner-main">
          <div className="banner-main-card">
            <PromoSpot
              promo={chosenSecondaryPromo}
              onCtaClick={(promo) => handlePromoClick(promo, { track: false })}
              onExpand={(promo) => trackPatientPromoEvent(promo, "view")}
              onClickTrack={(promo) => trackPatientPromoEvent(promo, "click")}
            />
          </div>
        </section>
      )}

      {/* NEW: INLINE TOAST FOR NOTIFICATIONS */}
      {notification && (
        <div className="home-toast">
          <span className="home-toast-dot" />
          <span className="home-toast-text">{notification}</span>
        </div>
      )}
    </div>
  );
}
